import React, { useEffect, useRef, useState } from "react";
import { QRCodeCanvas } from "qrcode.react";

import { useAuth } from "../../contextos/AuthContext";
import { estilosGrupos } from "../../elementos/gruposEstilos";
import {
  asegurarInvitaciones,
  enlaceInvitacion,
  renovarInvitacion,
} from "../../funciones/gruposServicio";

const TEXTOS = {
  estudiante: {
    pestana: "Estudiantes",
    ayuda:
      "Quien abra el enlace o use el código podrá solicitar unirse como estudiante. Si no tiene cuenta, la crea en ese momento. Cada solicitud la aprueba el administrador del grupo.",
    mensaje: (grupo, url, codigo) =>
      `Te invito a unirte a mi grupo "${grupo}" en Yutake para preparar tu examen. Entra aquí: ${url} (o usa el código ${codigo}).`,
    corto: (grupo, codigo) =>
      `Te invito a unirte a mi grupo "${grupo}" en Yutake. Código: ${codigo}.`,
  },
  profesor: {
    pestana: "Profesores",
    ayuda:
      "Para colegas que acompañarán al grupo. Podrán ver el avance de los estudiantes e invitar a otros, pero no administrar el grupo. El administrador aprueba cada solicitud.",
    mensaje: (grupo, url, codigo) =>
      `Te invito a acompañar como profesor el grupo "${grupo}" en Yutake. Entra aquí: ${url} (o usa el código ${codigo}).`,
    corto: (grupo, codigo) =>
      `Te invito a acompañar como profesor el grupo "${grupo}" en Yutake. Código: ${codigo}.`,
  },
};

export default function ModalInvitar({ grupo, abierto, onCerrar }) {
  const { usuario } = useAuth();

  const [rol, setRol] = useState("estudiante");
  const [codigos, setCodigos] = useState(null);
  const [cargando, setCargando] = useState(false);
  const [error, setError] = useState("");
  const [aviso, setAviso] = useState("");

  const qrRef = useRef(null);

  useEffect(() => {
    if (!abierto || !grupo || !usuario) return undefined;

    let activo = true;

    setRol("estudiante");
    setCargando(true);
    setError("");
    setCodigos(null);

    asegurarInvitaciones(grupo, usuario.uid)
      .then((resultado) => activo && setCodigos(resultado))
      .catch((err) => {
        console.error("Error cargando invitaciones:", err);
        if (activo) setError("No fue posible cargar los enlaces de invitación. Revisa la conexión.");
      })
      .finally(() => activo && setCargando(false));

    return () => {
      activo = false;
    };
  }, [abierto, grupo, usuario]);

  useEffect(() => {
    if (!abierto) return undefined;
    const alPresionar = (e) => e.key === "Escape" && onCerrar();
    window.addEventListener("keydown", alPresionar);
    return () => window.removeEventListener("keydown", alPresionar);
  }, [abierto, onCerrar]);

  if (!abierto || !grupo) return null;

  const codigo = codigos?.[rol];
  const url = codigo ? enlaceInvitacion(codigo) : "";
  const textos = TEXTOS[rol];
  const mensaje = codigo ? textos.mensaje(grupo.nombre, url, codigo) : "";
  const corto = codigo ? textos.corto(grupo.nombre, codigo) : "";
  const puedeCompartirNativo = typeof navigator !== "undefined" && !!navigator.share;

  const mostrarAviso = (texto) => {
    setAviso(texto);
    setTimeout(() => setAviso(""), 2500);
  };

  const copiar = async (texto, etiqueta) => {
    try {
      await navigator.clipboard.writeText(texto);
      mostrarAviso(`${etiqueta} copiado`);
    } catch (e) {
      mostrarAviso("No se pudo copiar. Selecciona el texto y cópialo manualmente.");
    }
  };

  const compartirNativo = async () => {
    try {
      await navigator.share({ title: `Invitación a ${grupo.nombre}`, text: corto, url });
    } catch (e) {
      // El usuario cerró el menú de compartir: no es un error
    }
  };

  const descargarQR = () => {
    const canvas = qrRef.current?.querySelector("canvas");
    if (!canvas) return;
    const enlace = document.createElement("a");
    enlace.href = canvas.toDataURL("image/png");
    enlace.download = `invitacion-${rol}-${codigo}.png`;
    enlace.click();
  };

  const renovar = async () => {
    if (!window.confirm("El enlace, el código y el QR actuales dejarán de funcionar. ¿Generar unos nuevos?")) return;

    setCargando(true);
    setError("");

    try {
      const nuevo = await renovarInvitacion(grupo, rol, codigo, usuario.uid);
      setCodigos((previo) => ({ ...previo, [rol]: nuevo }));
      mostrarAviso("Enlace renovado");
    } catch (err) {
      console.error("Error renovando invitación:", err);
      setError("No fue posible renovar el enlace.");
    } finally {
      setCargando(false);
    }
  };

  const enc = encodeURIComponent;

  const redes = [
    { nombre: "WhatsApp", href: `https://wa.me/?text=${enc(mensaje)}` },
    { nombre: "Telegram", href: `https://t.me/share/url?url=${enc(url)}&text=${enc(corto)}` },
    { nombre: "Facebook", href: `https://www.facebook.com/sharer/sharer.php?u=${enc(url)}` },
    { nombre: "Correo", href: `mailto:?subject=${enc(`Invitación a ${grupo.nombre}`)}&body=${enc(mensaje)}` },
  ];

  return (
    <div className="gg gg-fondo" onMouseDown={(e) => e.target === e.currentTarget && onCerrar()}>
      <style>{estilosGrupos}</style>

      <div className="gg-modal gg-modal--ancho" role="dialog" aria-modal="true" aria-labelledby="gg-titulo-invitar">
        <div className="gg-modal__cabecera">
          <div>
            <h2 id="gg-titulo-invitar">Invitar al grupo</h2>
            <p className="gg-sutil">{grupo.nombre}</p>
          </div>
          <button type="button" className="gg-cerrar" onClick={onCerrar} aria-label="Cerrar">×</button>
        </div>

        <div className="gg-pestanas" role="tablist">
          {Object.entries(TEXTOS).map(([clave, info]) => (
            <button
              key={clave}
              type="button"
              role="tab"
              aria-selected={rol === clave}
              className={`gg-pestana ${rol === clave ? "is-activo" : ""}`}
              onClick={() => setRol(clave)}
            >
              {info.pestana}
            </button>
          ))}
        </div>

        {error && <p role="alert" className="gg-alerta">{error}</p>}

        {cargando && !codigo && <p className="gg-sutil">Preparando enlaces…</p>}

        {codigo && (
          <div className="gg-bloque">
            <p className="gg-sutil">{textos.ayuda}</p>

            <div className="gg-invitacion">
              <div className="gg-qr" ref={qrRef}>
                <QRCodeCanvas value={url} size={168} level="M" includeMargin />
                <button type="button" className="gg-btn gg-btn--chico" onClick={descargarQR}>
                  Descargar QR
                </button>
              </div>

              <div className="gg-bloque">
                <div>
                  <span className="gg-sutil">Código</span>
                  <div className="gg-codigo">{codigo}</div>
                </div>

                <div className="gg-enlace-caja">
                  <span>{url}</span>
                  <button type="button" className="gg-btn gg-btn--chico" onClick={() => copiar(url, "Enlace")}>
                    Copiar
                  </button>
                </div>

                <div className="gg-compartir">
                  {puedeCompartirNativo && (
                    <button type="button" className="gg-btn gg-btn--primario" onClick={compartirNativo}>
                      Compartir…
                    </button>
                  )}
                  {redes.map((red) => (
                    <a key={red.nombre} className="gg-btn" href={red.href} target="_blank" rel="noopener noreferrer">
                      {red.nombre}
                    </a>
                  ))}
                  <button type="button" className="gg-btn" onClick={() => copiar(mensaje, "Mensaje")}>
                    Copiar mensaje
                  </button>
                </div>

                <p className="gg-sutil">
                  Para Instagram, usa "Compartir…" desde el celular o pega el mensaje copiado.
                </p>
              </div>
            </div>

            {grupo.esAdmin && (
              <div>
                <button type="button" className="gg-btn gg-btn--chico" onClick={renovar} disabled={cargando}>
                  Renovar enlace y código
                </button>
              </div>
            )}
          </div>
        )}
      </div>

      {aviso && <div className="gg-aviso-flotante" role="status">{aviso}</div>}
    </div>
  );
}